import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {Button, Checkbox, Form, Input, Popconfirm, Select, Space} from 'antd';
import {observer} from 'mobx-react-lite';
import {CustomerState} from '../../../store/users/customers/CustomerState';
import {FormShell} from '../../../components/form/FormShell';
import {countryPrefixes} from '../../../components/form/PhoneInput';
import {Spinner} from '../../../components/ui/Spinner';
import classNames from 'classnames';
import i18n from '../../../i18n/i18n';

export const CustomerEditView: React.FC = observer((): JSX.Element => {
  const {t} = useTranslation();
  const {customerId} = useParams();
  const [state] = useState(() => new CustomerState());
  const [showCorrespondence, setShowCorrespondence] = useState(true);
  const [showCompany, setShowCompany] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    customerId && state.loadCustomer(parseInt(customerId ?? '0'));
  }, [customerId, state]);

  useEffect(() => {
    form.setFieldsValue(state.item);
  }, [form, state.item]);

  return (
    <>
      {state.isLoading && (
        <div className="absolute top-0 left-0 w-full h-full bg-white opacity-50 z-10 h-screen flex justify-center items-center skip-fade">
          <Spinner />
        </div>
      )}
      <FormShell>
        <Space direction="vertical" className="w-full">
          <Form
            form={form}
            onFinish={(values) => {
              state.save({
                ...values,
                isCompany: values.nip !== undefined,
                correspondenceAddress: showCorrespondence ? values.correspondenceAddress : undefined,
              });
            }}
            layout="vertical"
          >
            <Form.Item className={'flex justify-end'}>
              <Space wrap>
                <Popconfirm
                  key="delete"
                  placement="bottomRight"
                  title={t('Are you sure you want to remove this employee?')}
                  onConfirm={() => state.deleteCustomer()}
                  okText={t('Yes')}
                  cancelText={t('No')}
                >
                  <Button size="large" className={classNames(customerId ? 'visible' : 'invisible')}>
                    {t('Remove')}
                  </Button>
                </Popconfirm>{' '}
                <Button size="large" type="primary" htmlType="submit">
                  Save
                </Button>
              </Space>
            </Form.Item>
            {state.item.customerId ? (
              <Form.Item name={'customerId'} style={{display: 'none'}}>
                <Input type="text" />
              </Form.Item>
            ) : (
              ''
            )}
            <Form.Item label={t('Full name')} required>
              <Input.Group compact>
                <Form.Item
                  required
                  noStyle
                  name="firstName"
                  rules={[{required: true, message: String(t('First name is required.'))}]}
                >
                  <Input placeholder={t('First name')} style={{width: '50%'}} />
                </Form.Item>
                <Form.Item
                  required
                  noStyle
                  name="lastName"
                  rules={[{required: true, message: String(t('Last name is required.'))}]}
                >
                  <Input placeholder={t('Last name')} style={{width: '50%'}} />
                </Form.Item>
              </Input.Group>
            </Form.Item>
            <Form.Item
              name="phone"
              label={i18n.t('Phone number')}
              rules={[{required: true, message: 'Please input your phone number!'}]}
            >
              <Input
                addonBefore={
                  <Form.Item name={'phoneNumberPrefix'} noStyle>
                    <Select
                      showSearch
                      placeholder="Select country"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={countryPrefixes}
                      style={{width: '200px'}}
                    />
                  </Form.Item>
                }
              />
            </Form.Item>

            <Form.Item
              name="additionalPhone"
              label={i18n.t('Additional phone number')}
              rules={[{required: false, message: 'Please input your phone number.'}]}
            >
              <Input
                addonBefore={
                  <Form.Item name={'additionalPhoneNumberPrefix'} noStyle>
                    <Select
                      showSearch
                      placeholder="Select country"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={countryPrefixes}
                      style={{width: '200px'}}
                    />
                  </Form.Item>
                }
              />
            </Form.Item>

            <Form.Item
              required
              name="email"
              label={t('E-mail')}
              rules={[{required: true, message: String(t('E-mail is required.'))}]}
            >
              <Input placeholder={t('E-mail')} />
            </Form.Item>
            <>
              <Form.Item>
                <Checkbox checked={showCompany} onChange={() => setShowCompany(!showCompany)}>
                  {t('Is company')}
                </Checkbox>
              </Form.Item>

              <Form.Item label={t('Company Information')} required>
                <Input.Group compact>
                  <Form.Item noStyle name="nip">
                    <Input placeholder={t('NIP number')} style={{width: '50%'}} disabled={!showCompany} />
                  </Form.Item>
                  <Form.Item noStyle name="companyName">
                    <Input placeholder={t('Company name')} style={{width: '50%'}} disabled={!showCompany} />
                  </Form.Item>
                </Input.Group>
              </Form.Item>
            </>
            <Form.Item label={t('Billing Address')} required>
              <Input.Group compact>
                <Form.Item
                  name={['billAddress', 'street']}
                  noStyle
                  rules={[{required: true, message: String(t('Street is required.'))}]}
                >
                  <Input placeholder={t('Street')} style={{width: '50%'}} />
                </Form.Item>
                <Form.Item
                  name={['billAddress', 'buildingNumber']}
                  noStyle
                  rules={[{required: true, message: String(t('Building number is required'))}]}
                >
                  <Input placeholder={t('Building number')} style={{width: '25%'}} />
                </Form.Item>
                <Form.Item name={['billAddress', 'appartmentNumber']} noStyle>
                  <Input placeholder={t('Apartment number')} style={{width: '25%'}} />
                </Form.Item>
              </Input.Group>
              <Space direction="vertical" size="small" className="w-full">
                <Input.Group compact>
                  <Form.Item
                    name={['billAddress', 'postalCode']}
                    noStyle
                    rules={[{required: true, message: String(t('Postal code is required'))}]}
                  >
                    <Input placeholder={t('Postal code')} style={{width: '50%'}} />
                  </Form.Item>
                  <Form.Item
                    name={['billAddress', 'city']}
                    noStyle
                    rules={[{required: true, message: String(t('City is required'))}]}
                  >
                    <Input placeholder={t('City')} style={{width: '50%'}} />
                  </Form.Item>
                </Input.Group>
              </Space>
            </Form.Item>
            <>
              <Form.Item>
                <Checkbox checked={showCorrespondence} onChange={() => setShowCorrespondence(!showCorrespondence)}>
                  {t('Different correspondence address')}
                </Checkbox>
              </Form.Item>
              {/*{!showCorrespondence ? (*/}
              {/*  ''*/}
              {/*) : (*/}
              <Form.Item label={t('Correspondence Address')}>
                <Input.Group compact>
                  <Form.Item name={['correspondenceAddress', 'street']} noStyle>
                    <Input placeholder={t('Street')} style={{width: '50%'}} disabled={!showCorrespondence} />
                  </Form.Item>
                  <Form.Item name={['correspondenceAddress', 'buildingNumber']} noStyle>
                    <Input placeholder={t('Building number')} style={{width: '25%'}} disabled={!showCorrespondence} />
                  </Form.Item>
                  <Form.Item name={['correspondenceAddress', 'appartmentNumber']} noStyle>
                    <Input placeholder={t('Apartment number')} style={{width: '25%'}} disabled={!showCorrespondence} />
                  </Form.Item>
                </Input.Group>
                <Space direction="vertical" size="small" className="w-full">
                  <Input.Group compact>
                    <Form.Item name={['correspondenceAddress', 'postalCode']} noStyle>
                      <Input placeholder={t('Postal code')} style={{width: '50%'}} disabled={!showCorrespondence} />
                    </Form.Item>
                    <Form.Item name={['correspondenceAddress', 'city']} noStyle>
                      <Input placeholder={t('City')} style={{width: '50%'}} disabled={!showCorrespondence} />
                    </Form.Item>
                  </Input.Group>
                </Space>
              </Form.Item>
              {/*)}*/}
            </>
          </Form>
        </Space>
      </FormShell>
    </>
  );
});
